import moment, { Moment } from "moment";
import type { DealBuilderInputStore } from "../../stores/deal-builder-inputs.store.ts";
import type { SerialBond } from "../pages/debt-service-page.tsx";
import type { PrimaryErrorEntry } from "../../analysis/hooks/useAnalysis.ts";
import Result from "./result.utils.ts";
import {
    DATE_FMT,
    DEFAULT_COUPON,
    deriveFirstCouponDate,
    isOnSemiannualGrid,
    parseDate,
    parseEnum,
} from "./pdf-extraction.utils.ts";
import {
    ExpandedRatingOptions,
    RatingAssumption,
    SaleMethod,
    SecurityType,
    TaxDesignation,
    USState,
} from "../services/utils/primary-constants.ts";

// Mirrors LOWEST_POSSIBLE_MARKET_CONDITIONS_DATE in deal-builder-inputs.utils.
const LOWEST_MARKET_CONDITIONS_DATE = moment("2021-06-01", DATE_FMT);

export type PrimaryErrorsPrefill = {
    inputPatch: Partial<DealBuilderInputStore>,
    bonds: SerialBond[],
    termRanges: [Moment, Moment][],
};

/**
 * The backend serialises parquet date columns as full ISO-8601 timestamps at
 * UTC midnight, while the deal builder works with local moments. Taking the
 * date part keeps a maturity on the day it was actually issued regardless of
 * the browser's timezone.
 */
function parseBackendDate(value: string | null | undefined): Moment | undefined {
    if (value == null) return undefined;
    return parseDate(value.slice(0, 10));
}

/**
 * The validator requires the first principal date to sit on the semiannual
 * grid that starts at the first coupon date.
 */
function isOnCouponGrid(firstCoupon: Moment, firstPrincipal: Moment): boolean {
    const months = firstPrincipal.diff(firstCoupon, "months", true);
    return months >= 0 && Math.abs(months % 6) < 0.05;
}

/**
 * Consecutive maturities flagged as sinkable are the sinking fund payments of
 * one term bond. They are injected as serial bonds and regrouped once the deal
 * is built, which is the same trick the PDF import uses.
 */
function findTermRanges(rows: { row: PrimaryErrorEntry, maturity: Moment }[]): [Moment, Moment][] {
    const ranges: [Moment, Moment][] = [];
    let run: Moment[] = [];

    const flush = () => {
        if (run.length > 1) ranges.push([run[0].clone(), run[run.length - 1].clone()]);
        run = [];
    };

    for (const { row, maturity } of rows) {
        const sinkable = row.sinkable === 1 || (row.sink_fund_type != null && row.sink_fund_type !== "");
        if (sinkable) run.push(maturity);
        else flush();
    }
    flush();

    return ranges;
}

/**
 * Maps the rows of one issue from the primary errors endpoint into a patch for
 * the deal builder input store plus the real maturity schedule.
 *
 * Unlike the PDF import there are no warnings or review fields here: this data
 * comes straight out of a parquet file, so it is deterministic and does not
 * need a human to double check it. Only genuinely unusable data is reported,
 * as an error.
 */
export function mapPrimaryErrorsToPrefill(
    entries: PrimaryErrorEntry[],
    current: DealBuilderInputStore,
): Result<PrimaryErrorsPrefill> {
    if (entries.length === 0) return { valid: false, error: "This issue has no bonds to load." };

    const inputPatch: Partial<DealBuilderInputStore> = {};
    const first = entries[0];

    // Report titles
    // The source names carry trailing padding.
    if (first.issuer_long_name) inputPatch.borrower = first.issuer_long_name.trim();
    if (first.issue_description) inputPatch.issuanceTitle = first.issue_description.trim();

    // The deal is priced against the market on the day it came to market.
    const offeringDate = parseBackendDate(first.offering_date);
    if (!offeringDate) return { valid: false, error: "This issue has no offering date, so it can't be priced." };
    if (offeringDate.isBefore(LOWEST_MARKET_CONDITIONS_DATE)) {
        return {
            valid: false,
            error: `This issue was offered on ${offeringDate.format("MMMM D, YYYY")}, before the ${LOWEST_MARKET_CONDITIONS_DATE.format("MMMM D, YYYY")} limit of our market data.`,
        };
    }
    inputPatch.marketConditionsDate = offeringDate;

    // Enum backed pricing assumptions. The backend already stores these in the
    // same long form the deal builder uses.
    const state = parseEnum(USState, first.state_long);
    if (state !== undefined) inputPatch.stateOfIssuance = state;

    const securityType = parseEnum(SecurityType, first.security_code);
    if (securityType !== undefined) inputPatch.securityType = securityType;

    const taxDesignation = parseEnum(TaxDesignation, first.tax_designation);
    if (taxDesignation !== undefined) inputPatch.taxDesignation = taxDesignation;

    const saleMethod = parseEnum(SaleMethod, first.offering_type_long);
    if (saleMethod !== undefined) inputPatch.saleMethod = saleMethod;

    // spline_lt_rating_composite already reflects any enhancement, so when the
    // issue is state enhanced it is the enhanced rating and the underlying
    // rating becomes the assumption. Without a usable underlying rating there
    // is nothing to enhance, so the composite is used on its own.
    const composite = parseEnum(RatingAssumption, first.spline_lt_rating_composite);
    const underlying = parseEnum(RatingAssumption, first.underlying_rating_str);
    const enhanced = parseEnum(ExpandedRatingOptions, first.spline_lt_rating_composite);

    if (first.state_enhancement === 1 && underlying !== undefined && enhanced !== undefined) {
        inputPatch.ratingAssumption = underlying;
        inputPatch.useStateEnhancement = true;
        inputPatch.stateEnhancement = enhanced;
    } else {
        if (composite !== undefined) inputPatch.ratingAssumption = composite;
        inputPatch.useStateEnhancement = false;
        inputPatch.stateEnhancement = undefined;
    }

    // insurance is the 0/1 flag. bond_insurance_code is populated even for
    // uninsured deals, so it can't be used to decide this.
    if (first.insurance != null) inputPatch.bondInsurance = first.insurance === 1;

    // Amortization assumptions
    const deliveryDate = parseBackendDate(first.delivery_date) ?? parseBackendDate(first.dated_date);
    if (deliveryDate) inputPatch.deliveryDate = deliveryDate;

    if (first.denomination_amount) inputPatch.denomination = first.denomination_amount;

    // Maturity schedule
    const rows = entries
        .map(row => ({ row, maturity: parseBackendDate(row.maturity_date) }))
        .filter((x): x is { row: PrimaryErrorEntry, maturity: Moment } => x.maturity !== undefined)
        .sort((a, b) => a.maturity.diff(b.maturity));

    if (rows.length === 0) return { valid: false, error: "This issue has no valid maturity dates." };

    if (!isOnSemiannualGrid(rows.map(x => x.maturity))) {
        return { valid: false, error: "This issue's maturities are not on a regular semiannual grid, so it can't be loaded." };
    }

    // The coupon is stored as a percentage but the deal builder works in rates.
    const bonds: SerialBond[] = rows.map(({ row, maturity }) => ({
        type: "serial",
        maturity: maturity.toISOString(),
        principal: row.total_maturity_offering_amt ?? 0,
        coupon: row.coupon != null ? row.coupon / 100 : DEFAULT_COUPON,
        lockedPrincipal: row.total_maturity_offering_amt != null,
        lockedCoupon: row.coupon != null,
    }));

    const termRanges = findTermRanges(rows);

    const firstPrincipalDate = rows[0].maturity.clone();
    const finalPrincipalDate = rows[rows.length - 1].maturity.clone();
    inputPatch.firstPrincipalDate = firstPrincipalDate;
    inputPatch.finalPrincipalDate = finalPrincipalDate;

    const semiannual = rows.some((x, i) =>
        i > 0 && x.maturity.diff(rows[i - 1].maturity, "months", true) < 7);
    inputPatch.principalRate = semiannual ? 2 : 1;

    // Par amount: trust the schedule when it is complete, otherwise fall back
    // to the stated issue size.
    const principals = rows.map(x => x.row.total_maturity_offering_amt);
    if (principals.every(x => x != null)) {
        inputPatch.parAmount = (principals as number[]).reduce((a, b) => a + b, 0);
    } else if (first.total_offering_amount) {
        inputPatch.parAmount = first.total_offering_amount;
    }

    // First coupon date: prefer the real one, but only when it keeps the first
    // principal on the semiannual grid the validator expects.
    const effectiveDelivery = deliveryDate ?? current.deliveryDate;
    const statedFirstCoupon = parseBackendDate(first.first_coupon_date);
    inputPatch.firstCouponDate = statedFirstCoupon && isOnCouponGrid(statedFirstCoupon, firstPrincipalDate) ?
        statedFirstCoupon :
        deriveFirstCouponDate(firstPrincipalDate, effectiveDelivery);

    // Call date. Maturities before the call are flagged "N", so take the first
    // call date the issue actually has, and drop it if it falls outside the
    // schedule.
    const callDate = rows
        .map(x => parseBackendDate(x.row.next_call_date))
        .filter((x): x is Moment => x !== undefined)
        .sort((a, b) => a.diff(b))[0];

    if (callDate && !callDate.isAfter(finalPrincipalDate, "day")) {
        inputPatch.useCallDate = true;
        inputPatch.callDate = callDate;
    } else {
        inputPatch.useCallDate = false;
    }

    return { valid: true, body: { inputPatch, bonds, termRanges } };
}
